"use client";

import { useAction, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import {
  AlertCircle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Check,
  FileImage,
  FileText,
  Loader2,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ANALYTICS_EVENTS, analyticsErrorType, track } from "@/lib/analyticsEvents";
import { cn } from "@/lib/utils";
import {
  TEMPLATE_DOCUMENT_MAX_BYTES,
  TEMPLATE_IMAGE_MAX_BYTES,
  TEMPLATE_MAX_IMAGES,
  TEMPLATE_MAX_PDF_PAGES,
  TEMPLATE_SCOPE_OPTIONS,
  type SavedTemplate,
  type TemplateScope,
  type TemplateSourceKind,
  type TemplateStyleProfile,
} from "./templateTypes";

const ACCEPT =
  "image/jpeg,image/png,image/webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const STEPS = ["Upload", "Review", "Configure", "Save"] as const;
const ANALYSIS_TIMEOUT_MS = 90_000;
type FlowStep = 0 | 1 | 2 | 3;

type AnalysisResult = {
  styleProfile: TemplateStyleProfile;
  detectedLessonType: Exclude<TemplateScope, "default"> | null;
  suggestedName: string;
  warnings: string[];
};

function isPdf(file: File): boolean {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

function isDocx(file: File): boolean {
  return (
    file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    file.name.toLowerCase().endsWith(".docx")
  );
}

function sourceKind(files: File[]): TemplateSourceKind {
  if (files[0] && isPdf(files[0])) return "pdf";
  if (files[0] && isDocx(files[0])) return "docx";
  return "image";
}

function validateFiles(files: File[]): string | null {
  if (files.length === 0) return "Choose a PDF, Word document, or 1–3 page images.";
  const fingerprints = files.map((file) => `${file.name}:${file.size}:${file.lastModified}`);
  if (new Set(fingerprints).size !== fingerprints.length) {
    return "The same page was selected more than once. Remove the duplicate and try again.";
  }
  const special = files.filter((file) => isPdf(file) || isDocx(file));
  if (special.length > 0 && files.length !== 1) {
    return "Upload one PDF or Word document at a time. Images can be uploaded together.";
  }
  if (special[0] && special[0].size > TEMPLATE_DOCUMENT_MAX_BYTES) {
    return "This document is over 10 MB. Export a smaller copy or upload page images.";
  }
  if (special.length === 0) {
    if (files.length > TEMPLATE_MAX_IMAGES) return "Upload up to 3 page images.";
    for (const file of files) {
      if (!IMAGE_TYPES.has(file.type)) return "Use JPG, PNG, WebP, PDF, or Word (.docx).";
      if (file.size > TEMPLATE_IMAGE_MAX_BYTES) return "Each page image must be 3 MB or smaller.";
    }
  }
  return null;
}

async function renderPdf(data: ArrayBuffer): Promise<{
  buffers: ArrayBuffer[];
  totalPages: number;
}> {
  const { getDocumentProxy, renderPageAsImage } = await import("unpdf");
  const pdf = await getDocumentProxy(new Uint8Array(data));
  const count = Math.min(TEMPLATE_MAX_PDF_PAGES, pdf.numPages);
  if (count < 1) throw new Error("This PDF has no readable pages.");
  const buffers: ArrayBuffer[] = [];
  for (let page = 1; page <= count; page += 1) {
    buffers.push(await renderPageAsImage(pdf, page, { scale: 1.35 }));
  }
  return { buffers, totalPages: pdf.numPages };
}

function uploadLabel(kind: TemplateSourceKind): string {
  if (kind === "pdf") return "PDF";
  if (kind === "docx") return "Word";
  return "Image";
}

function safeAnalysisError(error: unknown, kind: TemplateSourceKind): string {
  const message = error instanceof Error ? error.message.toLowerCase() : "";
  if (message.includes("timed out")) {
    return "Analysis took too long. Your selected file is still here—try again.";
  }
  if (message.includes("over 3 mb")) {
    return "A converted page is over 3 MB. Upload a lower-resolution image instead.";
  }
  if (message.includes("readable text")) {
    return "Darsi could not find readable text in this Word document. Upload page images instead.";
  }
  if (message.includes("no clear lesson structure")) {
    return "No clear lesson structure was detected. Try a page that shows the lesson stages.";
  }
  if (message.includes("no readable pages") || (kind === "pdf" && message.includes("pdf"))) {
    return "Darsi could not convert this PDF. Export a new PDF or upload the first pages as images.";
  }
  if (message.includes("upload did not finish")) {
    return "The upload did not finish. Check your connection and try again.";
  }
  return "Darsi could not analyze this lesson plan. Try a clearer scan or another file.";
}

function safeSaveError(error: unknown): string {
  const message = error instanceof Error ? error.message.toLowerCase() : "";
  if (message.includes("already exists")) {
    return "A template with this name already exists. Choose another name.";
  }
  if (message.includes("save up to")) {
    return "Your template library is full. Delete an unused template, then try again.";
  }
  if (message.includes("template not found")) {
    return "This template was removed in another session. Close this flow and refresh your library.";
  }
  if (message.includes("source is incomplete") || message.includes("source again")) {
    return "The analyzed source is no longer available. Upload and analyze it again.";
  }
  return "Darsi could not save this template. Your review is still here—try again.";
}

export function TemplateCreationFlow({
  open,
  onClose,
  templateCount,
  replaceTemplate,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  templateCount: number;
  replaceTemplate?: SavedTemplate | null;
  onSaved: (templateId: Id<"user_templates">) => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const generateUploadUrl = useMutation(api.files.generateUploadUrl);
  const deleteUploadedFiles = useMutation(api.files.deleteUploadedFiles);
  const analyzeTemplate = useAction(api.actions.extractTemplate.analyzeTemplate);
  const saveReviewed = useMutation(api.userTemplates.saveReviewed);

  const [step, setStep] = useState<FlowStep>(0);
  const [files, setFiles] = useState<File[]>([]);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [storageIds, setStorageIds] = useState<Id<"_storage">[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [pagesAnalyzed, setPagesAnalyzed] = useState(0);
  const [ignoredPages, setIgnoredPages] = useState(0);
  const [name, setName] = useState(replaceTemplate?.name ?? "");
  const [scope, setScope] = useState<TemplateScope>(replaceTemplate?.scope ?? "default");
  const [isDefault, setIsDefault] = useState(replaceTemplate?.isDefault ?? false);
  const [reviewed, setReviewed] = useState(false);
  const [status, setStatus] = useState<"idle" | "preparing" | "uploading" | "analyzing" | "saving">("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const reviewedTrackedRef = useRef(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      if (!startedRef.current) {
        startedRef.current = true;
        track(ANALYTICS_EVENTS.templateCreationStarted, {
          is_first_template: templateCount === 0,
          mode: replaceTemplate ? "replace" : "create",
        });
      }
    }
    if (!open && dialog.open) dialog.close();
  }, [open, replaceTemplate, templateCount]);

  const kind = files.length ? sourceKind(files) : null;
  const busy = status !== "idle";

  const cleanupPreviews = useCallback(() => {
    for (const url of previews) {
      if (url.startsWith("blob:")) URL.revokeObjectURL(url);
    }
  }, [previews]);

  const reset = useCallback(() => {
    cleanupPreviews();
    setStep(0);
    setFiles([]);
    setAnalysis(null);
    setStorageIds([]);
    setPreviews([]);
    setPagesAnalyzed(0);
    setIgnoredPages(0);
    setName(replaceTemplate?.name ?? "");
    setScope(replaceTemplate?.scope ?? "default");
    setIsDefault(replaceTemplate?.isDefault ?? false);
    setReviewed(false);
    setStatus("idle");
    setStatusMessage("");
    setError(null);
    startedRef.current = false;
    completedRef.current = false;
    reviewedTrackedRef.current = false;
  }, [cleanupPreviews, replaceTemplate]);

  const closeFlow = useCallback(() => {
    if (busy) return;
    if (startedRef.current && !completedRef.current) {
      track(ANALYTICS_EVENTS.templateCreationAbandoned, {
        step: ["upload", "review", "configure", "save"][step] as "upload" | "review" | "configure" | "save",
        had_analysis: Boolean(analysis),
      });
    }
    if (!completedRef.current && storageIds.length > 0) {
      void deleteUploadedFiles({ lessonImageIds: storageIds, guideImageIds: [] });
    }
    reset();
    onClose();
  }, [analysis, busy, deleteUploadedFiles, onClose, reset, step, storageIds]);

  const chooseFiles = useCallback(
    (nextFiles: File[]) => {
      if (nextFiles.length === 0) {
        if (storageIds.length > 0) {
          void deleteUploadedFiles({ lessonImageIds: storageIds, guideImageIds: [] });
        }
        cleanupPreviews();
        setFiles([]);
        setPreviews([]);
        setAnalysis(null);
        setStorageIds([]);
        setPagesAnalyzed(0);
        setIgnoredPages(0);
        setError(null);
        return;
      }
      const validation = validateFiles(nextFiles);
      setError(validation);
      if (validation) return;
      if (storageIds.length > 0) {
        void deleteUploadedFiles({ lessonImageIds: storageIds, guideImageIds: [] });
      }
      cleanupPreviews();
      setFiles(nextFiles);
      setAnalysis(null);
      setStorageIds([]);
      setPreviews(
        sourceKind(nextFiles) === "image"
          ? nextFiles.map((file) => URL.createObjectURL(file))
          : [],
      );
      setPagesAnalyzed(nextFiles.length);
      setIgnoredPages(0);
      setReviewed(false);
      const selectedKind = sourceKind(nextFiles);
      track(ANALYTICS_EVENTS.templateFileSelected, {
        file_type: selectedKind,
        page_count: selectedKind === "image" ? nextFiles.length : 1,
      });
    },
    [cleanupPreviews, deleteUploadedFiles, storageIds],
  );

  const uploadPage = useCallback(
    async (file: File): Promise<Id<"_storage">> => {
      if (file.size > TEMPLATE_IMAGE_MAX_BYTES) {
        throw new Error("A converted page is over 3 MB. Upload a lower-resolution image instead.");
      }
      const url = await generateUploadUrl({});
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      const body = (await response.json()) as { storageId?: string };
      if (!response.ok || !body.storageId) throw new Error("The upload did not finish. Try again.");
      return body.storageId as Id<"_storage">;
    },
    [generateUploadUrl],
  );

  const handleAnalyze = useCallback(async () => {
    const validation = validateFiles(files);
    if (validation) {
      setError(validation);
      return;
    }
    const currentKind = sourceKind(files);
    const startedAt = Date.now();
    const uploadedIds: Id<"_storage">[] = [];
    setError(null);
    setStatus("preparing");
    setStatusMessage(
      currentKind === "pdf"
        ? "Reading the first two PDF pages"
        : currentKind === "docx"
          ? "Reading the Word document structure"
          : "Preparing the lesson-plan pages",
    );
    try {
      if (storageIds.length > 0) {
        await deleteUploadedFiles({ lessonImageIds: storageIds, guideImageIds: [] });
        setStorageIds([]);
      }
      let html: string | undefined;
      let analyzedCount = files.length;
      let ignored = 0;
      if (currentKind === "pdf") {
        const rendered = await renderPdf(await files[0]!.arrayBuffer());
        cleanupPreviews();
        const pageFiles = rendered.buffers.map(
          (buffer, index) => new File([buffer], `page-${index + 1}.png`, { type: "image/png" }),
        );
        setPreviews(pageFiles.map((file) => URL.createObjectURL(file)));
        analyzedCount = pageFiles.length;
        ignored = Math.max(0, rendered.totalPages - analyzedCount);
        setStatus("uploading");
        for (let index = 0; index < pageFiles.length; index += 1) {
          setStatusMessage(`Uploading analyzed page ${index + 1} of ${pageFiles.length}`);
          uploadedIds.push(await uploadPage(pageFiles[index]!));
        }
      } else if (currentKind === "docx") {
        const mammoth = await import("mammoth");
        const result = await mammoth.convertToHtml({ arrayBuffer: await files[0]!.arrayBuffer() });
        html = result.value.trim();
        if (!html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").trim()) {
          throw new Error("Darsi could not find readable text in this Word document. Upload page images instead.");
        }
        analyzedCount = 1;
      } else {
        setStatus("uploading");
        for (let index = 0; index < files.length; index += 1) {
          setStatusMessage(`Uploading page ${index + 1} of ${files.length}`);
          uploadedIds.push(await uploadPage(files[index]!));
        }
      }
      setStorageIds(uploadedIds);
      setPagesAnalyzed(analyzedCount);
      setIgnoredPages(ignored);
      track(ANALYTICS_EVENTS.templateFileUploaded, {
        file_type: currentKind,
        page_count: analyzedCount,
      });
      setStatus("analyzing");
      setStatusMessage("Identifying lesson stages, sequence, timing, and teaching approach");
      track(ANALYTICS_EVENTS.templateAnalysisStarted, {
        file_type: currentKind,
        page_count: analyzedCount,
      });
      if (replaceTemplate) {
        track(ANALYTICS_EVENTS.templateReanalysisStarted, { file_type: currentKind });
      }
      const result = await Promise.race([
        analyzeTemplate({
          sourceStorageIds: uploadedIds.length ? uploadedIds : undefined,
          extractedHtml: html,
          sourceKind: currentKind,
          pagesAnalyzed: analyzedCount,
        }),
        new Promise<never>((_, reject) => {
          window.setTimeout(
            () => reject(new Error("Template analysis timed out")),
            ANALYSIS_TIMEOUT_MS,
          );
        }),
      ]);
      const nextAnalysis = result as AnalysisResult;
      setAnalysis(nextAnalysis);
      setName(replaceTemplate?.name ?? nextAnalysis.suggestedName);
      setScope(replaceTemplate?.scope ?? nextAnalysis.detectedLessonType ?? "default");
      setIsDefault(replaceTemplate?.isDefault ?? false);
      setStep(1);
      track(ANALYTICS_EVENTS.templateAnalysisCompleted, {
        file_type: currentKind,
        page_count: analyzedCount,
        detected_stage_count: nextAnalysis.styleProfile.stageSequence.length,
        detected_lesson_type: nextAnalysis.detectedLessonType,
        analysis_duration_ms: Date.now() - startedAt,
      });
      if (replaceTemplate) {
        track(ANALYTICS_EVENTS.templateReanalysisCompleted, {
          detected_stage_count: nextAnalysis.styleProfile.stageSequence.length,
        });
      }
    } catch (caught) {
      if (uploadedIds.length > 0) {
        void deleteUploadedFiles({ lessonImageIds: uploadedIds, guideImageIds: [] });
        setStorageIds([]);
      }
      setError(safeAnalysisError(caught, currentKind));
      track(ANALYTICS_EVENTS.templateAnalysisFailed, {
        file_type: currentKind,
        error_category: analyticsErrorType(caught),
      });
      track(ANALYTICS_EVENTS.templateCreationFailed, {
        error_category: analyticsErrorType(caught),
        step: "upload",
      });
    } finally {
      setStatus("idle");
      setStatusMessage("");
    }
  }, [analyzeTemplate, cleanupPreviews, deleteUploadedFiles, files, replaceTemplate, storageIds, uploadPage]);

  const updateProfile = useCallback(
    (update: (profile: TemplateStyleProfile) => TemplateStyleProfile) => {
      setAnalysis((current) => (current ? { ...current, styleProfile: update(current.styleProfile) } : current));
    },
    [],
  );

  const changeStage = useCallback(
    (index: number, key: "stage" | "typicalTimingMinutes", value: string) => {
      updateProfile((profile) => ({
        ...profile,
        stageSequence: profile.stageSequence.map((stage, stageIndex) =>
          stageIndex === index
            ? {
                ...stage,
                [key]: key === "typicalTimingMinutes" ? (value ? Number(value) : undefined) : value,
              }
            : stage,
        ),
      }));
    },
    [updateProfile],
  );

  const moveStage = useCallback(
    (index: number, direction: -1 | 1) => {
      updateProfile((profile) => {
        const next = [...profile.stageSequence];
        const target = index + direction;
        if (target < 0 || target >= next.length) return profile;
        [next[index], next[target]] = [next[target]!, next[index]!];
        return { ...profile, stageSequence: next };
      });
      track(ANALYTICS_EVENTS.templateAnalysisCorrected, { correction_type: "stage_order" });
    },
    [updateProfile],
  );

  const removeStage = useCallback(
    (index: number) => {
      updateProfile((profile) => ({
        ...profile,
        stageSequence: profile.stageSequence.filter((_, stageIndex) => stageIndex !== index),
      }));
      track(ANALYTICS_EVENTS.templateAnalysisCorrected, { correction_type: "stage_removed" });
    },
    [updateProfile],
  );

  const handleSave = useCallback(async () => {
    if (!analysis || !kind) return;
    if (!reviewed) {
      setError("Review the detected structure before saving.");
      return;
    }
    if (!name.trim()) {
      setError("Enter a template name.");
      return;
    }
    setError(null);
    setStatus("saving");
    setStatusMessage("Saving your template");
    try {
      const id = await saveReviewed({
        templateId: replaceTemplate?._id,
        name: name.trim(),
        scope,
        isDefault,
        sourceFileStorageIds: storageIds,
        sourceKind: kind,
        pagesAnalyzed,
        detectedLessonType: analysis.detectedLessonType ?? undefined,
        styleProfile: analysis.styleProfile,
      });
      completedRef.current = true;
      track(ANALYTICS_EVENTS.templateCreated, {
        scope,
        is_default: isDefault,
        detected_stage_count: analysis.styleProfile.stageSequence.length,
        is_first_template: templateCount === 0,
      });
      onSaved(id);
      reset();
      onClose();
    } catch (caught) {
      setError(safeSaveError(caught));
      track(ANALYTICS_EVENTS.templateCreationFailed, {
        error_category: analyticsErrorType(caught),
        step: "save",
      });
    } finally {
      setStatus("idle");
      setStatusMessage("");
    }
  }, [analysis, isDefault, kind, name, onClose, onSaved, pagesAnalyzed, replaceTemplate, reset, reviewed, saveReviewed, scope, storageIds, templateCount]);

  const canContinueReview = Boolean(
    analysis && analysis.styleProfile.stageSequence.length > 0 && reviewed,
  );
  const saveMissing = useMemo(() => {
    if (!analysis) return "Analyze a lesson plan first.";
    if (!reviewed) return "Review the detected structure.";
    if (!name.trim()) return "Enter a template name.";
    if (!scope) return "Select where this template applies.";
    if (analysis.styleProfile.stageSequence.length === 0) return "Keep at least one detected stage.";
    return null;
  }, [analysis, name, reviewed, scope]);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="template-flow-title"
      className="template-dialog m-auto max-h-[min(92dvh,900px)] w-[min(960px,calc(100%-1.5rem))] overflow-hidden rounded-2xl border border-border bg-card p-0 text-foreground shadow-xl backdrop:bg-[var(--modal-backdrop)]"
      onCancel={(event) => {
        if (busy) event.preventDefault();
        else closeFlow();
      }}
      onClose={() => {
        if (open) closeFlow();
      }}
    >
      <div className="flex max-h-[min(92dvh,900px)] min-h-0 flex-col">
        <header className="flex items-start justify-between gap-4 border-b border-border px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-muted-foreground">
              {replaceTemplate ? "Replace and reanalyze" : "Add template"}
            </p>
            <h2 id="template-flow-title" className="mt-0.5 text-xl font-bold tracking-tight">
              {STEPS[step]}
            </h2>
          </div>
          <button
            type="button"
            onClick={closeFlow}
            disabled={busy}
            aria-label="Close template creation"
            className="template-icon-button"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <ol className="grid grid-cols-4 border-b border-border bg-muted/25 px-4 sm:px-6" aria-label="Template creation progress">
          {STEPS.map((label, index) => (
            <li
              key={label}
              aria-current={index === step ? "step" : undefined}
              className={cn(
                "flex min-w-0 items-center gap-2 border-b-2 py-3 text-xs font-semibold",
                index === step ? "border-success text-foreground" : index < step ? "border-transparent text-success" : "border-transparent text-muted-foreground",
              )}
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-current text-[10px]">
                {index < step ? <Check className="h-3 w-3" /> : index + 1}
              </span>
              <span className="truncate">{label}</span>
            </li>
          ))}
        </ol>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
          {step === 0 ? (
            <section aria-labelledby="upload-heading" className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(260px,.65fr)]">
              <div className="min-w-0">
                <h3 id="upload-heading" className="text-lg font-bold">Upload one of your lesson plans</h3>
                <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">
                  Choose a plan that represents the structure and teaching approach you want Darsi to follow.
                </p>
                <div
                  className={cn(
                    "mt-5 rounded-xl border border-dashed p-5 text-center",
                    files.length ? "border-success/50 bg-muted/25" : "border-input bg-background",
                  )}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    chooseFiles(Array.from(event.dataTransfer.files));
                  }}
                >
                  <UploadCloud className="mx-auto h-7 w-7 text-success" />
                  <p className="mt-3 text-sm font-semibold">Drop the lesson plan here</p>
                  <p className="mt-1 text-sm text-muted-foreground">or choose it from your device</p>
                  <button type="button" autoFocus onClick={() => fileInputRef.current?.click()} className="template-secondary-button mt-4">
                    Choose file
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT}
                    multiple
                    className="sr-only"
                    onChange={(event) => {
                      chooseFiles(Array.from(event.target.files ?? []));
                      event.target.value = "";
                    }}
                  />
                </div>

                {files.length > 0 ? (
                  <div className="mt-4 space-y-2" aria-label="Selected files">
                    {files.map((file, index) => (
                      <div key={`${file.name}-${file.size}-${index}`} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
                        {kind === "image" ? <FileImage className="h-5 w-5 text-success" /> : <FileText className="h-5 w-5 text-success" />}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold">{file.name}</p>
                          <p className="text-xs text-muted-foreground">{uploadLabel(kind!)} · {(file.size / 1024 / 1024).toFixed(1)} MB</p>
                        </div>
                        <button
                          type="button"
                          className="template-icon-button"
                          aria-label={`Remove ${file.name}`}
                          onClick={() => chooseFiles(files.filter((_, fileIndex) => fileIndex !== index))}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="text-sm font-semibold text-success underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      Replace file
                    </button>
                  </div>
                ) : null}
              </div>

              <aside className="space-y-4 lg:border-l lg:border-border lg:pl-6">
                <div>
                  <h4 className="text-sm font-bold">What Darsi reads</h4>
                  <dl className="mt-3 space-y-3 text-sm">
                    <div><dt className="font-semibold">PDF</dt><dd className="text-muted-foreground">First 2 pages · up to 10 MB</dd></div>
                    <div><dt className="font-semibold">Page images</dt><dd className="text-muted-foreground">JPG, PNG, or WebP · up to 3 pages · 3 MB each</dd></div>
                    <div><dt className="font-semibold">Word (.docx)</dt><dd className="text-muted-foreground">Document text and table structure · up to 10 MB</dd></div>
                  </dl>
                </div>
                <div className="rounded-xl bg-muted/45 p-4">
                  <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-success" /><h4 className="text-sm font-bold">Protected document handling</h4></div>
                  <p className="mt-2 text-xs leading-5 text-muted-foreground">
                    Your signed-in account can open retained source previews. Darsi sends the selected pages or extracted Word content to its configured AI service only to detect reusable structure. Uploaded page images are kept with the saved template and deleted when you delete or replace it. Word files are read in your browser; the original file is not uploaded or retained.
                  </p>
                </div>
              </aside>
            </section>
          ) : null}

          {step === 1 && analysis ? (
            <section aria-labelledby="review-heading" className="space-y-6">
              <div>
                <h3 id="review-heading" className="text-lg font-bold">Review what Darsi detected</h3>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">Correct the reusable structure now. Future lesson content will still come from each new lesson.</p>
              </div>
              {analysis.warnings.length > 0 || ignoredPages > 0 ? (
                <div className="rounded-xl border border-warning/40 bg-warning-muted p-4 text-sm" role="status">
                  <p className="font-bold">Review note</p>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
                    {analysis.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                    {ignoredPages > 0 ? <li>{ignoredPages} later PDF page{ignoredPages === 1 ? " was" : "s were"} not analyzed.</li> : null}
                  </ul>
                </div>
              ) : null}
              <dl className="grid gap-3 rounded-xl border border-border bg-muted/20 p-4 sm:grid-cols-2 lg:grid-cols-4">
                <div><dt className="text-xs font-bold text-muted-foreground">Suggested name</dt><dd className="mt-1 text-sm font-semibold">{analysis.suggestedName}</dd></div>
                <div>
                  <dt className="text-xs font-bold text-muted-foreground">Detected lesson type</dt>
                  <dd className="mt-1">
                    <select
                      value={analysis.detectedLessonType ?? ""}
                      onChange={(event) => {
                        const detectedLessonType = (event.target.value || null) as AnalysisResult["detectedLessonType"];
                        setAnalysis((current) => current ? { ...current, detectedLessonType } : current);
                        track(ANALYTICS_EVENTS.templateAnalysisCorrected, { correction_type: "lesson_type" });
                      }}
                      className="template-input"
                    >
                      <option value="">Not clearly detected</option>
                      {TEMPLATE_SCOPE_OPTIONS.filter((option) => option.value !== "default").map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </dd>
                </div>
                <div><dt className="text-xs font-bold text-muted-foreground">Pages analyzed</dt><dd className="mt-1 text-sm font-semibold">{pagesAnalyzed}</dd></div>
                <div><dt className="text-xs font-bold text-muted-foreground">Analysis status</dt><dd className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-success"><Check className="h-4 w-4" />Ready to review</dd></div>
              </dl>
              <div className="grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(250px,.7fr)]">
                <div className="min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="font-bold">Lesson sequence</h4>
                    <span className="text-sm text-muted-foreground">{analysis.styleProfile.stageSequence.length} detected stages</span>
                  </div>
                  <ol className="mt-3 space-y-2">
                    {analysis.styleProfile.stageSequence.map((stage, index) => (
                      <li key={`${index}-${stage.stage}`} className="rounded-xl border border-border p-3">
                        <div className="grid gap-3 sm:grid-cols-[2rem_minmax(0,1fr)_7rem_auto] sm:items-start">
                          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-sm font-bold">{index + 1}</span>
                          <label className="min-w-0 text-xs font-semibold text-muted-foreground">
                            Stage name
                            <input value={stage.stage} onChange={(event) => changeStage(index, "stage", event.target.value)} onBlur={() => track(ANALYTICS_EVENTS.templateAnalysisCorrected, { correction_type: "stage_name" })} className="template-input mt-1" />
                          </label>
                          <label className="text-xs font-semibold text-muted-foreground">
                            Minutes
                            <input type="number" min={1} max={300} value={stage.typicalTimingMinutes ?? ""} onChange={(event) => changeStage(index, "typicalTimingMinutes", event.target.value)} onBlur={() => track(ANALYTICS_EVENTS.templateAnalysisCorrected, { correction_type: "timing" })} className="template-input mt-1" />
                          </label>
                          <div className="flex gap-1">
                            <button type="button" className="template-icon-button" disabled={index === 0} onClick={() => moveStage(index, -1)} aria-label={`Move ${stage.stage} earlier`}><ArrowUp className="h-4 w-4" /></button>
                            <button type="button" className="template-icon-button" disabled={index === analysis.styleProfile.stageSequence.length - 1} onClick={() => moveStage(index, 1)} aria-label={`Move ${stage.stage} later`}><ArrowDown className="h-4 w-4" /></button>
                            <button type="button" className="template-icon-button text-destructive" disabled={analysis.styleProfile.stageSequence.length <= 1} onClick={() => removeStage(index)} aria-label={`Remove ${stage.stage}`}><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </div>
                        {(stage.purpose || stage.teacherAction || stage.studentAction || stage.typicalInteraction || stage.assessmentStrategy) ? (
                          <dl className="mt-3 grid gap-2 border-t border-border pt-3 text-xs sm:grid-cols-2">
                            {stage.purpose ? <div><dt className="font-bold">Purpose</dt><dd className="mt-0.5 text-muted-foreground">{stage.purpose}</dd></div> : null}
                            {stage.typicalInteraction ? <div><dt className="font-bold">Interaction</dt><dd className="mt-0.5 text-muted-foreground">{stage.typicalInteraction}</dd></div> : null}
                            {stage.teacherAction ? <div><dt className="font-bold">Teacher action</dt><dd className="mt-0.5 text-muted-foreground">{stage.teacherAction}</dd></div> : null}
                            {stage.studentAction ? <div><dt className="font-bold">Learner action</dt><dd className="mt-0.5 text-muted-foreground">{stage.studentAction}</dd></div> : null}
                            {stage.assessmentStrategy ? <div><dt className="font-bold">Checking</dt><dd className="mt-0.5 text-muted-foreground">{stage.assessmentStrategy}</dd></div> : null}
                          </dl>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                </div>
                <aside className="space-y-5">
                  <div>
                    <h4 className="font-bold">Teaching approach</h4>
                    <dl className="mt-3 space-y-3 text-sm">
                      <div><dt className="text-xs font-bold text-muted-foreground">Structure</dt><dd>{analysis.styleProfile.teachingApproach ?? `${analysis.styleProfile.procedureDetailLevel} procedures in a ${analysis.styleProfile.tone.toLowerCase()} tone.`}</dd></div>
                      {analysis.styleProfile.interactionPatterns.length ? <div><dt className="text-xs font-bold text-muted-foreground">Interaction</dt><dd>{analysis.styleProfile.interactionPatterns.join(" · ")}</dd></div> : null}
                      {analysis.styleProfile.differentiationApproach ? <div><dt className="text-xs font-bold text-muted-foreground">Differentiation</dt><dd>{analysis.styleProfile.differentiationApproach}</dd></div> : null}
                      {analysis.styleProfile.assessmentApproach ? <div><dt className="text-xs font-bold text-muted-foreground">Assessment</dt><dd>{analysis.styleProfile.assessmentApproach}</dd></div> : null}
                    </dl>
                  </div>
                  <label className="block text-sm font-bold">
                    Instruction Darsi will follow
                    <textarea
                      value={analysis.styleProfile.writingStyleSummary}
                      onChange={(event) => {
                        updateProfile((profile) => ({ ...profile, writingStyleSummary: event.target.value }));
                      }}
                      onBlur={() => track(ANALYTICS_EVENTS.templateAnalysisCorrected, { correction_type: "summary" })}
                      className="template-input mt-2 min-h-28 resize-y"
                    />
                  </label>
                  {previews.length ? (
                    <div>
                      <h4 className="text-sm font-bold">Analyzed pages</h4>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {previews.map((url, index) => (
                          <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="group block overflow-hidden rounded-lg border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={url} alt={`Analyzed page ${index + 1}`} className="aspect-[4/5] w-full object-cover object-top" />
                            <span className="block border-t border-border px-2 py-1.5 text-xs font-semibold">Page {index + 1}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl bg-muted/45 p-4 text-xs leading-5 text-muted-foreground">
                      <FileText className="mb-2 h-4 w-4" />The Word document was read in your browser. Its original file is not stored for preview.
                    </div>
                  )}
                  <label className="flex items-start gap-3 rounded-xl border border-border p-4 text-sm">
                    <input type="checkbox" checked={reviewed} onChange={(event) => setReviewed(event.target.checked)} className="mt-0.5 h-4 w-4 accent-[var(--success)]" />
                    <span><strong className="block">I reviewed this structure</strong><span className="mt-1 block text-xs leading-5 text-muted-foreground">The stage order and instruction describe what Darsi should follow.</span></span>
                  </label>
                </aside>
              </div>
            </section>
          ) : null}

          {step === 2 && analysis ? (
            <section aria-labelledby="configure-heading" className="mx-auto max-w-2xl space-y-6">
              <div>
                <h3 id="configure-heading" className="text-lg font-bold">Configure template usage</h3>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">Choose where Darsi should suggest this structure. Default status is separate from lesson type.</p>
              </div>
              <label className="block text-sm font-bold">
                Template name
                <input value={name} maxLength={80} onChange={(event) => setName(event.target.value)} className="template-input mt-2" aria-describedby="template-name-help" />
                <span id="template-name-help" className="mt-1.5 block text-xs font-normal text-muted-foreground">This name stays inside your private template library.</span>
              </label>
              <fieldset>
                <legend className="text-sm font-bold">Applies to</legend>
                <p className="mt-1 text-xs text-muted-foreground">Darsi automatically suggests this template for the selected lesson type.</p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {TEMPLATE_SCOPE_OPTIONS.map((option) => (
                    <label key={option.value} className={cn("flex min-h-11 items-center gap-3 rounded-lg border px-3 py-2 text-sm font-semibold", scope === option.value ? "border-success bg-muted/45" : "border-border")}>
                      <input type="radio" name="scope" value={option.value} checked={scope === option.value} onChange={() => { setScope(option.value); track(ANALYTICS_EVENTS.templateScopeSelected, { scope: option.value }); }} className="accent-[var(--success)]" />
                      {option.label}
                    </label>
                  ))}
                </div>
              </fieldset>
              <label className="flex items-start gap-3 rounded-xl border border-border p-4">
                <input type="checkbox" checked={isDefault} onChange={(event) => { setIsDefault(event.target.checked); track(ANALYTICS_EVENTS.templateDefaultSelected, { is_default: event.target.checked }); }} className="mt-1 h-4 w-4 accent-[var(--success)]" />
                <span><strong className="block text-sm">Use as my default template</strong><span className="mt-1 block text-xs leading-5 text-muted-foreground">Used when no more specific lesson-type template is selected. Only one template can be the global default.</span></span>
              </label>
            </section>
          ) : null}

          {step === 3 && analysis ? (
            <section aria-labelledby="save-heading" className="mx-auto max-w-2xl">
              <div className="text-center">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-success"><LockKeyhole className="h-5 w-5" /></div>
                <h3 id="save-heading" className="mt-3 text-lg font-bold">Ready to save</h3>
                <p className="mt-1 text-sm text-muted-foreground">Check how this template will behave before adding it to your library.</p>
              </div>
              <dl className="mt-6 divide-y divide-border rounded-xl border border-border">
                <div className="grid grid-cols-[9rem_minmax(0,1fr)] gap-3 p-4"><dt className="text-sm font-semibold text-muted-foreground">Template</dt><dd className="text-sm font-bold">{name || "—"}</dd></div>
                <div className="grid grid-cols-[9rem_minmax(0,1fr)] gap-3 p-4"><dt className="text-sm font-semibold text-muted-foreground">Applies to</dt><dd className="text-sm">{TEMPLATE_SCOPE_OPTIONS.find((option) => option.value === scope)?.label}</dd></div>
                <div className="grid grid-cols-[9rem_minmax(0,1fr)] gap-3 p-4"><dt className="text-sm font-semibold text-muted-foreground">Default</dt><dd className="text-sm">{isDefault ? "Yes — global fallback" : "No"}</dd></div>
                <div className="grid grid-cols-[9rem_minmax(0,1fr)] gap-3 p-4"><dt className="text-sm font-semibold text-muted-foreground">Detected</dt><dd className="text-sm">{analysis.styleProfile.stageSequence.length} stages from {pagesAnalyzed} analyzed {pagesAnalyzed === 1 ? "page" : "pages"}</dd></div>
                <div className="grid grid-cols-[9rem_minmax(0,1fr)] gap-3 p-4"><dt className="text-sm font-semibold text-muted-foreground">Darsi follows</dt><dd className="text-sm leading-6">{analysis.styleProfile.writingStyleSummary}</dd></div>
              </dl>
            </section>
          ) : null}

          {status !== "idle" ? (
            <div className="mt-5 flex items-center gap-3 rounded-xl border border-success/35 bg-muted/45 p-4 text-sm" role="status" aria-live="polite">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-success" />
              <span>{statusMessage}</span>
            </div>
          ) : null}
          {error ? (
            <div className="mt-5 flex items-start gap-3 rounded-xl border border-destructive/35 bg-destructive/5 p-4 text-sm" role="alert">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <div className="min-w-0 flex-1"><p>{error}</p>{step === 0 && files.length ? <button type="button" onClick={() => void handleAnalyze()} className="mt-2 inline-flex items-center gap-2 font-bold underline underline-offset-4"><RefreshCw className="h-3.5 w-3.5" />Try again</button> : null}</div>
            </div>
          ) : null}
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-card px-4 py-4 sm:px-6">
          <button
            type="button"
            onClick={() => {
              if (step === 0) closeFlow();
              else setStep((step - 1) as FlowStep);
            }}
            disabled={busy}
            className="template-secondary-button"
          >
            <ArrowLeft className="h-4 w-4" />{step === 0 ? "Cancel" : "Back"}
          </button>
          {step === 0 ? (
            <button type="button" disabled={busy || files.length === 0} onClick={() => void handleAnalyze()} className="template-primary-button">
              Analyze lesson structure <ArrowRight className="h-4 w-4" />
            </button>
          ) : step === 1 ? (
            <button type="button" disabled={!canContinueReview || busy} onClick={() => { setStep(2); if (!reviewedTrackedRef.current) { reviewedTrackedRef.current = true; track(ANALYTICS_EVENTS.templateAnalysisReviewed, { detected_stage_count: analysis?.styleProfile.stageSequence.length ?? 0, detected_lesson_type: analysis?.detectedLessonType ?? null }); } }} className="template-primary-button">
              Configure usage <ArrowRight className="h-4 w-4" />
            </button>
          ) : step === 2 ? (
            <button type="button" disabled={!name.trim() || busy} onClick={() => setStep(3)} className="template-primary-button">
              Review and save <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <div className="flex flex-col items-end gap-1">
              <button type="button" disabled={Boolean(saveMissing) || busy} onClick={() => void handleSave()} className="template-primary-button">
                {status === "saving" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Save template
              </button>
              {saveMissing ? <p className="text-xs text-muted-foreground" role="status">{saveMissing}</p> : null}
            </div>
          )}
        </footer>
      </div>
    </dialog>
  );
}
